import { useState, useEffect } from 'react';
import { api, useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Plus, Trash2, Shield, ShieldOff, KeyRound } from 'lucide-react';
import { toast } from 'sonner';

export default function TeamPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmDeleteUserId, setConfirmDeleteUserId] = useState(null);
  const [resetPwUserId, setResetPwUserId] = useState(null);
  const [resetPwValue, setResetPwValue] = useState('');
  const isAdmin = user?.role === 'admin';

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      const res = await api.get('/users');
      setUsers(res.data.users);
    } catch (err) { console.error('Failed to fetch users:', err); }
  };

  const handleAddMember = async () => {
    if (!email.trim()) { toast.error('Email is required'); return; }
    if (!password.trim()) { toast.error('Password is required so the member can login'); return; }
    const domain = email.split('@')[1];
    if (!['autocarma.in', 'trycarpal.com'].includes(domain)) {
      toast.error('Only autocarma.in and trycarpal.com emails allowed');
      return;
    }
    try {
      await api.post('/users', {
        email: email.trim(),
        name: name.trim() || email.split('@')[0],
        password: password.trim(),
      });
      toast.success('Team member added');
      setDialogOpen(false);
      setEmail(''); setName(''); setPassword('');
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add member'); }
  };

  const handleRoleToggle = async (userId, currentRole) => {
    const newRole = currentRole === 'admin' ? 'member' : 'admin';
    try {
      await api.put(`/users/${userId}/role`, { role: newRole });
      toast.success(`Role updated to ${newRole}`);
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update role'); }
  };

  const handleDelete = (userId) => {
    setConfirmDeleteUserId(userId);
  };

  const confirmDelete = async () => {
    if (!confirmDeleteUserId) return;
    try {
      await api.delete(`/users/${confirmDeleteUserId}`);
      toast.success('Member removed'); fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to remove member'); }
    finally { setConfirmDeleteUserId(null); }
  };

  const handleResetPassword = async () => {
    if (!resetPwUserId || !resetPwValue.trim()) { toast.error('Password is required'); return; }
    try {
      await api.put(`/users/${resetPwUserId}/password`, { password: resetPwValue.trim() });
      toast.success('Password updated');
      setResetPwUserId(null); setResetPwValue('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update password'); }
  };

  return (
    <div data-testid="team-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-[#34495e]">Team</h2>
          <p className="text-sm text-[#7F8C8D]">{users.length} members</p>
        </div>
        {isAdmin && (
          <Button onClick={() => setDialogOpen(true)} className="bg-[#34495e] hover:bg-[#2c3e50] text-white rounded-sm" data-testid="add-member-btn">
            <Plus size={16} className="mr-1" /> Add Member
          </Button>
        )}
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-sm overflow-hidden">
        <table className="w-full" data-testid="team-table">
          <thead>
            <tr className="bg-[#F8F9FA]">
              <th className="text-left px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-[#7F8C8D]">Name</th>
              <th className="text-left px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-[#7F8C8D]">Email</th>
              <th className="text-left px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-[#7F8C8D]">Role</th>
              <th className="text-left px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-[#7F8C8D]">Joined</th>
              {isAdmin && <th className="text-left px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-[#7F8C8D]">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.user_id} className="border-b border-[#E2E8F0] last:border-0">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-sm bg-[#34495e] flex items-center justify-center text-white text-xs font-bold">
                      {(u.name || u.email)?.charAt(0)?.toUpperCase()}
                    </div>
                    <span className="text-sm text-[#34495e]">{u.name || u.email?.split('@')[0]}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-[#7F8C8D]">{u.email}</td>
                <td className="px-4 py-3">
                  <span className={`inline-block px-2 py-0.5 rounded-sm text-xs font-bold ${
                    u.role === 'admin' ? 'bg-[#34495e] text-white' : 'bg-[#E2E8F0] text-[#34495e]'
                  }`}>
                    {u.role === 'admin' ? 'Admin' : 'Member'}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-[#7F8C8D]">
                  {u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}
                </td>
                {isAdmin && (
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      {u.user_id !== user?.user_id && (
                        <>
                          <button
                            onClick={() => { setResetPwUserId(u.user_id); setResetPwValue(''); }}
                            className="p-1.5 text-[#7F8C8D] hover:text-[#f39c12] transition-colors"
                            title="Reset password"
                            data-testid={`reset-pw-${u.user_id}`}
                          >
                            <KeyRound size={14} />
                          </button>
                          <button
                            onClick={() => handleRoleToggle(u.user_id, u.role)}
                            className="p-1.5 text-[#7F8C8D] hover:text-[#34495e] transition-colors"
                            title={u.role === 'admin' ? 'Remove admin' : 'Make admin'}
                            data-testid={`toggle-role-${u.user_id}`}
                          >
                            {u.role === 'admin' ? <ShieldOff size={14} /> : <Shield size={14} />}
                          </button>
                          <button
                            onClick={() => handleDelete(u.user_id)}
                            className="p-1.5 text-[#7F8C8D] hover:text-[#e74c3c] transition-colors"
                            data-testid={`delete-user-${u.user_id}`}
                          >
                            <Trash2 size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-sm bg-white border border-[#E2E8F0] rounded-sm" data-testid="add-member-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-black tracking-tight text-[#34495e]">Add Team Member</DialogTitle>
            <DialogDescription className="sr-only">Add a new team member by email</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-[#7F8C8D]">Email</Label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@autocarma.in" className="mt-1 rounded-sm border-[#E2E8F0]" data-testid="member-email-input" />
              <p className="text-[10px] text-[#BDC3C7] mt-1">Only @autocarma.in and @trycarpal.com</p>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-[#7F8C8D]">Name (optional)</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" className="mt-1 rounded-sm border-[#E2E8F0]" data-testid="member-name-input" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-[#7F8C8D]">Password</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Set login password" className="mt-1 rounded-sm border-[#E2E8F0]" data-testid="member-password-input" />
              <p className="text-[10px] text-[#BDC3C7] mt-1">Member will use this to sign in. They can also use Slack login.</p>
            </div>
            <Button onClick={handleAddMember} className="w-full bg-[#34495e] hover:bg-[#2c3e50] text-white rounded-sm" data-testid="add-member-submit-btn">
              Add Member
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm Delete Member */}
      <AlertDialog open={!!confirmDeleteUserId} onOpenChange={(open) => { if (!open) setConfirmDeleteUserId(null); }}>
        <AlertDialogContent className="bg-white border border-[#E2E8F0] rounded-sm" data-testid="confirm-delete-member-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-lg font-black text-[#34495e]">Remove team member?</AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-[#7F8C8D]">
              This member will be removed from the team. Their tasks will not be deleted — they will be shown as "currently not in team".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm border-[#E2E8F0] text-[#7F8C8D]" data-testid="confirm-delete-member-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="rounded-sm bg-[#e74c3c] hover:bg-[#c0392b] text-white" data-testid="confirm-delete-member-yes">Yes, remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset Password Dialog */}
      <Dialog open={!!resetPwUserId} onOpenChange={(open) => { if (!open) { setResetPwUserId(null); setResetPwValue(''); } }}>
        <DialogContent className="max-w-sm bg-white border border-[#E2E8F0] rounded-sm" data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-black tracking-tight text-[#34495e]">Reset Password</DialogTitle>
            <DialogDescription className="text-sm text-[#7F8C8D]">
              Set a new password for this team member.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-[#7F8C8D]">New Password</Label>
              <Input
                type="password"
                value={resetPwValue}
                onChange={(e) => setResetPwValue(e.target.value)}
                placeholder="Enter new password"
                className="mt-1 rounded-sm border-[#E2E8F0]"
                data-testid="reset-pw-input"
                onKeyDown={(e) => { if (e.key === 'Enter') handleResetPassword(); }}
                autoFocus
              />
            </div>
            <Button onClick={handleResetPassword} className="w-full bg-[#34495e] hover:bg-[#2c3e50] text-white rounded-sm" data-testid="reset-pw-submit">
              Update Password
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
